import { useId, useState, useContext, useEffect } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { EditAssessmentPageContext } from '../context/EditAssessmentPage.context';

export interface UseAssessmentEditableFieldArgs {
  placeholder?: string;
  controlledContent: string;
  onExitEditMode?: (content: string) => void;
}

export function useAssessmentEditableField({
  controlledContent,
  onExitEditMode,
  placeholder,
}: UseAssessmentEditableFieldArgs) {
  const id = useId();
  const [isEditing, setIsEditing] = useState(false);
  const editAssessment = useContext(EditAssessmentPageContext);
  const [editableContent, setEditableContent] = useState(controlledContent);

  function enterEditMode() {
    setIsEditing(true);
    editAssessment?.setFieldInEditID(id);
  }

  function exitEditMode() {
    setIsEditing(false);
    editAssessment?.setFieldInEditID(null);
    const finalContent = editableContent.trim() || placeholder || '';
    onExitEditMode?.(finalContent);
  }

  useHotkeys(
    'esc',
    (e) => {
      e.stopPropagation();
      e.preventDefault();
      exitEditMode();
    },
    { enabled: isEditing, enableOnFormTags: ['textarea'] }
  );

  useEffect(() => {
    if (!isEditing) return;
    if (editAssessment?.isAddingAreaForImprovement) {
      editAssessment.setIsAddingAreaForImprovement(false);
    }
    if (editAssessment?.isAddingFinancialStrength) {
      editAssessment.setIsAddingFinancialStrength(false);
    }
  }, [editAssessment]);

  useEffect(() => {
    setEditableContent(controlledContent);
  }, [controlledContent]);

  return {
    isEditing,
    enterEditMode,
    exitEditMode,
    editAssessment,
    id,
    placeholder,
    setEditableContent,
    editableContent,
  };
}
